import { useEffect, useState } from "react";
import { Info, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { apiFetch } from "@/lib/auth";

const TYPE_STYLES = {
    info: {
        bg: "bg-indigo-50 text-indigo-900 border-indigo-100",
        icon: Info,
        iconClass: "text-indigo-600",
    },
    warning: {
        bg: "bg-amber-50 text-amber-900 border-amber-100",
        icon: AlertTriangle,
        iconClass: "text-amber-600",
    },
    success: {
        bg: "bg-emerald-50 text-emerald-900 border-emerald-100",
        icon: CheckCircle2,
        iconClass: "text-emerald-600",
    },
};

const DISMISS_KEY = "tiendify_dismissed_banners";

function getDismissed() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
        return new Set();
    }
}

function saveDismissed(set) {
    try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify([...set]));
    } catch {
        /* ignore */
    }
}

export default function BannerStrip({ scope = "home" }) {
    const [banners, setBanners] = useState([]);
    const [dismissed, setDismissed] = useState(() => getDismissed());

    useEffect(() => {
        let cancelled = false;
        apiFetch
            .get("/banners", { params: { scope } })
            .then((r) => {
                if (!cancelled) setBanners(Array.isArray(r.data) ? r.data : []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [scope]);

    const dismiss = (id) => {
        const next = new Set(dismissed);
        next.add(id);
        setDismissed(next);
        saveDismissed(next);
    };

    const visible = banners.filter((b) => b.id && !dismissed.has(b.id));
    if (visible.length === 0) return null;

    return (
        <div data-testid={`banner-strip-${scope}`}>
            {visible.map((b) => {
                const style = TYPE_STYLES[b.type] || TYPE_STYLES.info;
                const Icon = style.icon;
                return (
                    <div
                        key={b.id}
                        className={`border-b px-4 sm:px-6 py-2.5 text-sm ${style.bg}`}
                        role="status"
                    >
                        <div className="max-w-7xl mx-auto flex items-center gap-3">
                            <Icon className={`w-4 h-4 shrink-0 ${style.iconClass}`} />
                            <p className="flex-1 leading-snug">
                                {b.message}
                                {b.link && (
                                    <>
                                        {" "}
                                        <a
                                            href={b.link}
                                            target={b.link.startsWith("http") ? "_blank" : "_self"}
                                            rel="noreferrer"
                                            className="font-semibold underline underline-offset-2 hover:opacity-80"
                                        >
                                            {b.linkLabel || "Saber más"}
                                        </a>
                                    </>
                                )}
                            </p>
                            <button
                                onClick={() => dismiss(b.id)}
                                aria-label="Cerrar"
                                className="p-1 -m-1 opacity-60 hover:opacity-100"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
